import React, { useCallback, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Divider, Flex, Box, Table, Thead, Th, Tr, Tbody, Td } from '@televet/kibble-ui/build/chakra';
import { useTriggerAiRecapExportToPimsMutation, useUpdateClinicSettingsClinicMutation } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useAppDispatch } from 'state/hooks';
import { openAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';
import { useFailedWritebackAppointments } from 'shared/hooks/useFailedWritebackAppointments';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { Link } from '@televet/kibble-ui/build/components/Link';

const AiRecapSettings = (): JSX.Element => {
  const toast = useToast();
  const { currentClinic, currentClinicId } = useClinicUser();
  const { primaryIntegrationName } = useIntegrationsProvider();
  const { aiRecapWritebackEnabled } = currentClinic?.clinicSetting || {};
  const [writebackEnabled, setWritebackEnabled] = useState(aiRecapWritebackEnabled);
  const [updateClinicSettings] = useUpdateClinicSettingsClinicMutation();
  const dispatch = useAppDispatch();

  const { failedWritebackAppointments, refetchFailedWritebackAppointments } = useFailedWritebackAppointments();

  const [triggerAiRecapExportToPims, { loading: isExportingAiRecapToPims }] = useTriggerAiRecapExportToPimsMutation();

  const handleAiRecapWritebackEnabledToggle = useCallback(async (): Promise<void> => {
    try {
      if (currentClinicId && currentClinic?.clinicSetting) {
        await updateClinicSettings({
          variables: {
            id: currentClinicId,
            data: {
              clinicSetting: {
                update: {
                  where: {
                    id: {
                      equals: currentClinic?.clinicSetting?.id,
                    },
                  },
                  data: {
                    aiRecapWritebackEnabled: !writebackEnabled,
                  },
                },
              },
            },
          },
        });
        setWritebackEnabled((current) => !current);
        toast({
          title: 'Successfully toggled the AI Recap sync enable setting.',
          status: 'success',
        });
      }
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
      toast({
        title: 'There was an error toggling the AI Recap sync enable setting. Please try again.',
        status: 'error',
      });
    }
  }, [currentClinic?.clinicSetting, currentClinicId, toast, updateClinicSettings, writebackEnabled]);

  const handleRetrySync = useCallback(
    async (appointmentId: string): Promise<void> => {
      const response = await triggerAiRecapExportToPims({
        variables: {
          where: {
            id: appointmentId,
          },
        },
      });
      const { success } = response.data?.aiRecapExportToPims || {};
      refetchFailedWritebackAppointments();
      toast({
        title: success ? 'Sync Successful' : 'Sync Failed',
        description: success
          ? `AI Recap successfully written back to ${primaryIntegrationName}`
          : `AI Recap failed to write back to ${primaryIntegrationName}`,
        status: success ? 'success' : 'error',
      });
    },
    [refetchFailedWritebackAppointments, toast, triggerAiRecapExportToPims, primaryIntegrationName],
  );

  const handleViewDetails = useCallback(
    (appointmentId: string): void => {
      dispatch(
        openAppointmentModal({
          appointmentId,
        }),
      );
    },
    [dispatch],
  );

  const renderFailedAppointmentsTable = useMemo(() => {
    if (!failedWritebackAppointments || failedWritebackAppointments.length === 0) {
      return (
        <Text mt={2} variant="subtle" size="sm">
          No syncing errors in the last 48 hours.
        </Text>
      );
    }

    return (
      <Box mt={4} overflowX="auto">
        <Table variant="unstyled" size="sm">
          <Thead borderBottomWidth="1px" borderBottomColor="background.lessSubtle">
            <Tr>
              <Th textAlign="left">Patient Name</Th>
              <Th textAlign="center">Client Name</Th>
              <Th textAlign="center">Appointment Details</Th>
              <Th textAlign="center">Retry Sync</Th>
            </Tr>
          </Thead>
          <Tbody>
            {failedWritebackAppointments.map((appointment, index) => {
              const petName = appointment?.clinicPet?.name || 'Unknown';
              const petParents = appointment?.clinicPetParents || [];
              const clientName =
                petParents.length > 0
                  ? `${petParents[0].firstName || ''} ${petParents[0].lastName || ''}`.trim() || 'Unknown'
                  : 'Unknown';

              return (
                <Tr key={appointment.id} bgColor={index % 2 === 0 ? 'background.default' : 'transparent'}>
                  <Td textAlign="left">{petName}</Td>
                  <Td textAlign="center">{clientName}</Td>
                  <Td textAlign="center">
                    <Link size="sm" onClick={(): void => handleViewDetails(appointment.id)}>
                      View Details
                    </Link>
                  </Td>
                  <Td textAlign="center">
                    <Button
                      size="sm"
                      variant="ghostNeutral"
                      iconName="arrowCircle"
                      isLoading={isExportingAiRecapToPims}
                      onClick={(): Promise<void> => handleRetrySync(appointment.id)}
                    />
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  }, [failedWritebackAppointments, handleRetrySync, handleViewDetails, isExportingAiRecapToPims]);

  return (
    <Flex direction="column" mt={5}>
      <Switch
        size="sm"
        label={`Sync AI Recap to ${primaryIntegrationName}`}
        labelStyle={{ fontSize: 'sm', fontWeight: 'semibold' }}
        isChecked={writebackEnabled}
        isFieldInline
        onChange={handleAiRecapWritebackEnabledToggle}
      />
      <Text mt={1} variant="subtle" size="sm">
        When switched on, AI Recaps will be exported to {primaryIntegrationName} nightly at 12:00AM CT. Any syncing
        errors will be shown below
      </Text>
      <Divider mt={5} mb={5} />
      <Text fontWeight="bold" size="md">
        Syncing Errors
      </Text>
      {renderFailedAppointmentsTable}
    </Flex>
  );
};

export default AiRecapSettings;
